import { type Component, createMemo, createSignal, type ParentProps, Show } from 'solid-js'
import Button from '~/components/Button'
import Icon from '~/components/Icon'
import { api } from '~/services/api'
import { setAuthStore } from '~/stores/auth'
import { isNetworkError, offline, setOffline, setUnauthorized, unauthorized } from '~/stores/connection'

/**
 * Pantalla de bloqueo para errores de conexión o autenticación. Cubre dos
 * escenarios: backend caído (offline) y sesión inválida / sin permisos (401).
 * Reemplaza el contenido del layout admin para que nunca quede una página
 * en blanco.
 */
const ConnectionBoundary: Component<ParentProps> = props => {
  const [retrying, setRetrying] = createSignal(false)

  const reason = createMemo(() => {
    if (offline()) return 'offline'
    if (unauthorized()) return 'unauthorized'
    return null
  })

  const retry = async () => {
    setRetrying(true)
    try {
      await api.get('/ai/public/config')
      setOffline(false)
      setUnauthorized(false)
    } catch (error) {
      if (isNetworkError(error)) {
        setOffline(true)
      } else {
        // El servidor respondió (aunque sea 401): la conexión existe.
        // Si sigue 401, unauthorized ya está seteado por el interceptor.
        setOffline(false)
      }
    } finally {
      setRetrying(false)
    }
  }

  return (
    <>
      {/* Los children se mantienen montados: si la pantalla de bloqueo los
          desmontara, las páginas reinician sus peticiones al volver, y un 401
          persistente con otros requests 2xx del mismo lote oscila true/false
          y remonta la página en bucle (flood de peticiones en consola). */}
      <div class={reason() ? 'hidden' : ''} aria-hidden={Boolean(reason())}>
        {props.children}
      </div>
      <Show when={reason()}>
        {reason() === 'offline' ? (
          <div class="absolute inset-0 flex flex-col items-center justify-center text-center p-6 bg-surface">
            <div class="p-4 rounded-2xl bg-danger-soft mb-6">
              <Icon icon="material-symbols-light:wifi-off" size={56} class="text-danger" />
            </div>
            <h2 class="text-xl font-semibold text-foreground mb-2">Sin conexión con el servidor</h2>
            <p class="text-foreground-subtle max-w-sm mb-6">No pudimos comunicarnos con el servidor. Revisa tu conexión o que el servidor esté encendido, y vuelve a intentarlo.</p>
            <Button onClick={retry} loading={retrying()} prependIcon="material-symbols-light:refresh">
              Reintentar
            </Button>
          </div>
        ) : (
          <div class="absolute inset-0 flex flex-col items-center justify-center text-center p-6 bg-surface">
            <div class="p-4 rounded-2xl bg-warning-soft mb-6">
              <Icon icon="material-symbols-light:lock" size={56} class="text-warning" />
            </div>
            <h2 class="text-xl font-semibold text-foreground mb-2">Sin acceso</h2>
            <p class="text-foreground-subtle max-w-sm mb-6">Tu sesión no tiene permiso para acceder a este recurso, o la sesión expiró. Inicia sesión nuevamente para continuar.</p>
            <div class="flex gap-3">
              <Button onClick={retry} loading={retrying()} prependIcon="material-symbols-light:refresh">
                Reintentar
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setAuthStore({ token: null, user: null, companyId: null, companies: [] })
                  window.location.href = '/login'
                }}
                prependIcon="material-symbols-light:login"
              >
                Iniciar sesión
              </Button>
            </div>
          </div>
        )}
      </Show>
    </>
  )
}

export default ConnectionBoundary
